import React, { useState } from "react";
import EmailForm from "./EmailForm";

const ClientSuccess = () => {
  const [isEmailFormOpen, setIsEmailFormOpen] = useState(false);
  const [selectedService, setSelectedService] = useState("");

  const handleStartSuccessStory = () => {
    setSelectedService("Success Story Consultation");
    setIsEmailFormOpen(true);
  };

  const closeEmailForm = () => {
    setIsEmailFormOpen(false);
    setSelectedService("");
  };
  const caseStudies = [
    {
      id: "fintech-startup",
      company: "FinTech Startup",
      industry: "Financial Technology",
      size: "50-100 employees",
      challenge:
        "Rapid product development with no automation testing framework",
      solution:
        "Complete automation framework implementation with AI-powered test generation",
      results: [
        "70% reduction in manual testing effort",
        "90% test coverage achieved in 6 weeks",
        "50% faster release cycles",
        "Zero production bugs in last 6 months",
      ],
      technologies: ["TypeScript", "Playwright", "GitHub Actions", "Allure"],
      timeline: "4 weeks implementation + 2 weeks training",
      testimonial:
        "The AI-powered test generation completely transformed our QA process. We went from manual testing nightmares to a fully automated, scalable testing pipeline.",
      role: "CTO",
    },
    {
      id: "ecommerce-platform",
      company: "E-commerce Platform",
      industry: "Retail Technology",
      size: "100-200 employees",
      challenge: "Legacy Selenium tests were flaky and maintenance-heavy",
      solution:
        "Framework modernization with Playwright and strategic test architecture redesign",
      results: [
        "95% reduction in test flakiness",
        "60% faster test execution",
        "80% less maintenance overhead",
        "Improved developer confidence in deployments",
      ],
      technologies: ["Playwright", "TypeScript", "Jenkins", "Docker"],
      timeline: "6 weeks migration + ongoing support",
      testimonial:
        "Our old Selenium suite was a constant source of frustration. The new Playwright framework is rock-solid and has completely changed how we approach testing.",
      role: "Lead QA Engineer",
    },
    {
      id: "saas-platform",
      company: "SaaS Analytics Platform",
      industry: "Business Intelligence",
      size: "75 employees",
      challenge: "Scaling QA team capabilities without hiring more engineers",
      solution:
        "AI QA agent implementation with comprehensive team training and mentoring",
      results: [
        "3x increase in test productivity",
        "100% automated API test coverage",
        "Self-sufficient QA team in 3 months",
        "Established quality metrics and KPIs",
      ],
      technologies: ["Java", "REST Assured", "TestNG", "Allure"],
      timeline: "8 weeks implementation + 3 months mentoring",
      testimonial:
        "The mentoring program was exceptional. Our team went from automation beginners to experts, and the AI agent helps us maintain velocity even as we scale.",
      role: "Engineering Manager",
    },
  ];

  const metrics = [
    {
      value: "70%",
      label: "Average Test Development Time Reduction",
      icon: "⚡",
    },
    {
      value: "95%",
      label: "Test Coverage Improvement",
      icon: "📊",
    },
    {
      value: "60%",
      label: "Faster Release Cycles",
      icon: "🚀",
    },
    {
      value: "3x",
      label: "QA Team Productivity Increase",
      icon: "📈",
    },
  ];

  return (
    <>
      <section id="case-studies" className="py-20 bg-gray-900/30">
        <div className="container mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Client <span className="text-gradient">Success Stories</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Real results from real companies. See how our automation
              consulting has transformed QA practices and accelerated business
              outcomes.
            </p>
          </div>

          {/* Key Metrics */}
          <div className="grid md:grid-cols-4 gap-6 mb-20">
            {metrics.map((metric, index) => (
              <div
                key={index}
                className="text-center p-6 bg-gray-800/50 rounded-xl border border-gray-700/50"
              >
                <div className="text-4xl mb-2">{metric.icon}</div>
                <div className="text-3xl font-bold text-gradient mb-2">
                  {metric.value}
                </div>
                <div className="text-gray-400 text-sm">{metric.label}</div>
              </div>
            ))}
          </div>

          {/* Case Studies */}
          <div className="space-y-12 mb-20">
            {caseStudies.map((study, index) => (
              <div
                key={study.id}
                className="bg-gray-800/50 rounded-2xl p-8 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-300"
              >
                <div className="grid lg:grid-cols-3 gap-8">
                  {/* Company Info */}
                  <div className="lg:col-span-1">
                    <div className="mb-4">
                      <h3 className="text-2xl font-bold text-white mb-2">
                        {study.company}
                      </h3>
                      <div className="space-y-1 text-sm">
                        <p className="text-blue-400">{study.industry}</p>
                        <p className="text-gray-400">{study.size}</p>
                        <p className="text-purple-400">{study.timeline}</p>
                      </div>
                    </div>

                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-white mb-2">
                        Technologies Used
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {study.technologies.map((tech, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-gray-700/50 text-gray-300 rounded-full text-xs"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Challenge & Solution */}
                  <div className="lg:col-span-2">
                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <h4 className="text-lg font-semibold text-red-400 mb-3">
                          Challenge
                        </h4>
                        <p className="text-gray-300 text-sm">
                          {study.challenge}
                        </p>
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-green-400 mb-3">
                          Solution
                        </h4>
                        <p className="text-gray-300 text-sm">
                          {study.solution}
                        </p>
                      </div>
                    </div>

                    {/* Results */}
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold text-blue-400 mb-3">
                        Results Achieved
                      </h4>
                      <div className="grid md:grid-cols-2 gap-3">
                        {study.results.map((result, idx) => (
                          <div
                            key={idx}
                            className="flex items-center space-x-2"
                          >
                            <span className="text-green-400 text-sm">✓</span>
                            <span className="text-gray-300 text-sm">
                              {result}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Testimonial */}
                    <div className="bg-gray-900/50 rounded-lg p-4 border-l-4 border-blue-400">
                      <p className="text-gray-300 italic mb-2">
                        "{study.testimonial}"
                      </p>
                      <p className="text-blue-400 text-sm font-medium">
                        — {study.role}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center">
            <div className="bg-gradient-to-r from-gray-800/50 to-gray-900/50 rounded-2xl p-8 md:p-12 border border-gray-700/50">
              <h3 className="text-3xl font-bold text-white mb-4">
                Ready to Join Our Success Stories?
              </h3>
              <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
                Let's discuss how we can help transform your QA practice and
                achieve similar results for your engineering team.
              </p>
              <button
                onClick={handleStartSuccessStory}
                className="px-8 py-4 bg-gradient-to-r from-blue-400 to-blue-500 text-black font-bold rounded-lg hover:from-green-500 hover:to-blue-600 transition transform hover:scale-105"
              >
                Start Your Success Story
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Email Form Modal */}
      <EmailForm
        isOpen={isEmailFormOpen}
        onClose={closeEmailForm}
        selectedFramework={selectedService}
      />
    </>
  );
};

export default ClientSuccess;
